// src/utils/receipt/downloadReceiptPdf.js
// Generic, dependency-light PDF receipt builder.
// jsPDF is lazy-loaded so it only ships when a user actually downloads a receipt.

const COLORS = {
  text: '#1C252E',
  muted: '#637381',
  border: '#E3E8EF',
  soft: '#F6F8FA',
  brand: '#1877F2',
  white: '#FFFFFF',
};

const HEX_COLOR = /^#([0-9a-f]{6}|[0-9a-f]{3})$/i;

function safeColor(color, fallback) {
  if (typeof color === 'string' && HEX_COLOR.test(color)) return color;
  return fallback;
}

function hasValue(value) {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  if (!text) return false;
  if (text === '-') return false;
  return true;
}

/**
 * @param {object} receipt
 * @param {string} receipt.brandName
 * @param {string} receipt.title
 * @param {string} receipt.reference
 * @param {string} receipt.issuedAt
 * @param {{label: string, color?: string}} receipt.status
 * @param {{label: string, amount: string, token: string}[]} receipt.headline  (max 2 items)
 * @param {{title: string, rows: {label: string, value: any, mono?: boolean}[]}[]} receipt.sections
 * @param {string} receipt.footerNote
 * @param {string} receipt.fileName
 * @param {string} [receipt.brandColor]
 */
export async function downloadReceiptPdf(receipt) {
  // eslint-disable-next-line import/no-extraneous-dependencies
  const { jsPDF } = await import('jspdf');

  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  const footerSpace = 18;
  const accent = safeColor(receipt.brandColor, COLORS.brand);

  let y = 0;

  function ensureSpace(needed) {
    if (y + needed <= pageHeight - footerSpace) return;
    doc.addPage();
    y = margin;
  }

  // ── Header band ──────────────────────────────────────────────
  doc.setFillColor(accent);
  doc.rect(0, 0, pageWidth, 34, 'F');

  doc.setTextColor(COLORS.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(receipt.brandName, margin, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(receipt.title, margin, 24);

  doc.setFontSize(9);
  doc.text(`Ref: ${receipt.reference}`, pageWidth - margin, 16, { align: 'right' });
  doc.text(receipt.issuedAt, pageWidth - margin, 24, { align: 'right' });

  y = 46;

  // ── Status pill ──────────────────────────────────────────────
  const statusLabel = String(receipt.status?.label || 'Pending').toUpperCase();
  const statusColor = safeColor(receipt.status?.color, COLORS.muted);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  const pillWidth = doc.getTextWidth(statusLabel) + 8;
  doc.setFillColor(statusColor);
  doc.roundedRect(margin, y - 5, pillWidth, 7, 3.5, 3.5, 'F');
  doc.setTextColor(COLORS.white);
  doc.text(statusLabel, margin + 4, y);

  y += 8;

  // ── Headline amounts ─────────────────────────────────────────
  const headline = (receipt.headline || []).slice(0, 2);

  if (headline.length) {
    const boxHeight = 30;
    const columnWidth = contentWidth / headline.length;

    doc.setFillColor(COLORS.soft);
    doc.setDrawColor(COLORS.border);
    doc.roundedRect(margin, y, contentWidth, boxHeight, 3, 3, 'FD');

    headline.forEach((item, index) => {
      const x = margin + 6 + columnWidth * index;

      if (index > 0) {
        doc.setDrawColor(COLORS.border);
        doc.line(
          margin + columnWidth * index,
          y + 5,
          margin + columnWidth * index,
          y + boxHeight - 5
        );
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(COLORS.muted);
      doc.text(item.label, x, y + 9);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(COLORS.text);
      doc.text(String(item.amount || '-'), x, y + 19);

      doc.setFontSize(10);
      doc.setTextColor(accent);
      doc.text(String(item.token || ''), x, y + 25);
    });

    y += boxHeight + 10;
  }

  // ── Sections ─────────────────────────────────────────────────
  const labelWidth = 46;
  const valueWidth = contentWidth - labelWidth;
  const lineHeight = 3.8;

  (receipt.sections || []).forEach((section) => {
    const rows = (section.rows || []).filter((row) => hasValue(row.value));
    if (!rows.length) return;

    ensureSpace(18);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(COLORS.text);
    doc.text(section.title, margin, y);

    y += 2.5;
    doc.setDrawColor(COLORS.border);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;

    rows.forEach((row) => {
      let valueFont = 'helvetica';
      if (row.mono) valueFont = 'courier';

      doc.setFont(valueFont, 'bold');
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(String(row.value), valueWidth);
      const rowHeight = lines.length * lineHeight + 3;

      ensureSpace(rowHeight);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(COLORS.muted);
      doc.text(row.label, margin, y);

      doc.setFont(valueFont, 'bold');
      doc.setTextColor(COLORS.text);
      doc.text(lines, margin + labelWidth, y);

      y += rowHeight;
    });

    y += 4;
  });

  // ── Footer on every page ─────────────────────────────────────
  const pageCount = doc.getNumberOfPages();

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);

    const footerY = pageHeight - 12;
    doc.setDrawColor(COLORS.border);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(COLORS.muted);

    const noteLines = doc.splitTextToSize(receipt.footerNote || '', contentWidth - 25);
    doc.text(noteLines, margin, footerY);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - margin, footerY, { align: 'right' });
  }

  doc.save(receipt.fileName || 'receipt.pdf');
}

export default downloadReceiptPdf;
